<script context="module">
    export const SLICE_DOWNLOAD_TYPE_THUMBNAIL = "thumbnail";
    export const SLICE_DOWNLOAD_TYPE_PREVIEW = "preview";
    export const SLICE_DOWNLOAD_TYPE_BINARY = "binary";
    export const SLICE_DOWNLOAD_TYPE_ZIP = "zip";
    export const USE_FILE_ACCESS_API = true;
    export const USE_DOWNLOAD_CACHE = true;
</script>

<script>
    import { username, currentSession } from "../stores.js";
    import { extractSliceName, millisecondsToTime, durationInMilliseconds, now, sleep, isNumeric } from "../utils.js";
    import {
        downloader,
        showDownloader,
        downloadJobs,
        addDownloadJob,
        updateJob,
        interruptJob,
        isJobInterrupted,
        memcache,
        memcacheProgress,
    } from "../downloadstores.js";
    import { toast } from "@zerodevx/svelte-toast";
    import {
        toastThemeErrorDismissable,
        toastThemeInfo,
        toastThemeWarning,
    } from "../toasts.js";
    import Modal from "../components/Modal.svelte";
    import NoSleep from "nosleep.js";
    const noSleep = new NoSleep();

    // Singleton instance
    let downloaderComponent;
    export function getDownloaderComponent() {
        if (!downloaderComponent) {
            downloaderComponent = this;
            $downloader = downloaderComponent;
        }
        return downloaderComponent;
    }

    /////////////////////// BEGIN DOWNLOADING ///////////////////////

    function convertBlobToBase64(blob) {
        return new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onerror = reject;
            reader.onload = () => {
                resolve(reader.result);
            };
            reader.readAsDataURL(blob);
        });
    }

    function sliceIntoChunks(arr, chunkSize) {
        const res = [];
        for (let i = 0; i < arr.length; i += chunkSize) {
            const chunk = arr.slice(i, i + chunkSize);
            res.push(chunk);
        }
        return res;
    }

    // Function to take care of downloading a single file.
    // Using behavioural options and supporting aborting fetch.
    async function downloadFile(f, controller, dir, useSliceDownloadCache, stats) {
        // stats = {count, bytes, fetchTime, loadTime, writeTime}
        // Prepare for writing
        let writableStream;
        if (USE_FILE_ACCESS_API && dir) {
            const file = await dir.getFileHandle(f.name, { create: true });
            writableStream = await file.createWritable();
        }

        // Switch on chaosmonkey for debugging purposes
        const USE_CHAOSMONKEY = false;

        // Fetch a blob from source with a retries on normal errors like 404
        // which fetch does not reject and a delayed retry on network errors,
        // which fetch does reject.
        const MAX_RETRIES = 5;
        let retries = MAX_RETRIES;
        let filedata = undefined;
        let timerStart = performance.now();
        let timerEnd = undefined;
        while (retries >= 0) {
            // Chaos monkey: At random, say 10% of times, inject a CHAOSMONKEY string into the path
            let chaosmonkey = USE_CHAOSMONKEY && Math.random() < 0.1 ? "CHAOSMONKEY" : "";
            if (chaosmonkey) console.log("🐒: ", f.name, " 🙈");

            filedata = await fetch(f.path + chaosmonkey, {
                signal: controller.signal,
                headers: f.type ? {
                    "Content-Type": f.type,
                    "X-Content-Type-Options" : "nosniff"
                } : undefined
            }).catch(async () => {
                // Network error
                console.log(`RETRYING FILE ${f.name}...`);
                await sleep(5000);
                const retry = await fetch(f.path, {
                    priority: "low",
                    signal: controller.signal,
                    headers: f.type ? {
                        "Content-Type": f.type,
                        "X-Content-Type-Options" : "nosniff"
                    } : undefined
                }).catch((error) => {
                    console.log(`DOWNLOAD FILE ${f.name} FAILED: ${error}`);
                    f.status = "failed";
                    failedDownloads.push(f);
                    throw error;
                });
                return retry;
            });
            timerEnd = performance.now();
            stats.fetchTime += timerEnd - timerStart;

            if (!filedata.ok) {
                --retries;
                console.log(`RETRYING FILE ${f.name}...`);
            }
            if (filedata.ok) break;
        }

        if (!filedata.ok) {
            console.log(`DOWNLOAD FILE ${f.name} FAILED: ${filedata.status}`);
            f.status = "failed";
            failedDownloads.push(f);
            return Promise.reject(new Error(`${f.name} failed with ${filedata.status}`));
        }

        if (filedata.ok) {
            // Mark success
            f.status = "success";

            // Load blob into memory
            timerStart = performance.now();
            const blob = await filedata.blob();
            blob.name = f.name;
            if (useSliceDownloadCache && f.slice >= 0) {
                // Warning! Don't use with large datasets, because it
                // will run into out-of-memory
                $memcache[f.name] = {
                    slice: f.slice,
                    blob: await convertBlobToBase64(blob),
                };
            }
            timerEnd = performance.now();
            stats.loadTime += timerEnd - timerStart;

            stats.count += 1;
            stats.bytes += blob.size;

            // Write the blob to destination
            if (!useSliceDownloadCache) {
                if (USE_FILE_ACCESS_API && writableStream) {
                    const readableStream = blob.stream();
                    timerStart = performance.now();
                    await readableStream.pipeTo(writableStream);
                    timerEnd = performance.now();
                } else {
                    timerStart = performance.now();
                    saveBlob(blob);
                    timerEnd = performance.now();
                }
                stats.writeTime += timerEnd - timerStart;
            }
        }
    }

    //////// Example code that clients of onDownloadImg can use
    /*
    async function onViewReconstruction(name, url) {
        onDownloadImg(name, url, SLICE_DOWNLOAD_TYPE_THUMBNAIL, USE_DOWNLOAD_CACHE);
    }

    async function onDownloadReconstruction(name, url) {
        onDownloadImg(name, url, SLICE_DOWNLOAD_TYPE_BINARY);
    }
    */
    //////// END examples

    let failedDownloads = [];
    /**
     * Download image dataset
     * @param name e.g. 03-tomo-pre00-ph01-rec04
     * @param urlPattern e.g. https://cloudcttest.../datasets/ses-221014-104902-lu-mouse-heart/03-tomo-pre00-ph01-rec04/Output/slice_0_%04d.tif?...
     * @param minSlice e.g. 500
     * @param maxSlice e.g. 1999
     * @param sliceDownloadType e.g. thumbnail (or preview or binary)
     * @param useDownloadCache e.g. true if images should be cached (not reasonable for large datasets, e.g. tif)
     * @param includeReportUrl e.g. https://cloudcttest.../datasets/ses-230425-170825-pineapple-localized/03-tomo-pre00-ph01-rec03/Output/Binaries/report.pdf?...
     */
    export async function onDownloadImg(
        name,
        urlPattern,
        bin,
        minSlice,
        maxSlice,
        sliceDownloadType,
        useDownloadCache,
        batchSize,
        reductionFactor,
        includeReportUrl
    ) {
        if (useDownloadCache) {
            $memcache = {}; // Clear the cache to make room for new downloads
        }

        // Clear the current list of failed downloads.
        // TODO: Keep a failedDownloads for each job in $downloadJobs,
        // but perhaps avoid storing it in a job, since we do frequent
        // updating which may slow down operations.
        failedDownloads = [];

        // Download reconstructed images from known location
        console.log(`MY SESSION IS: ${$currentSession}`);
        console.log(`SELECT DATASET TO DOWNLOAD: ${$currentSession}, ${name}: `, urlPattern);

        let sliceFileType = "tif";
        let useSliceDownloadCache = useDownloadCache;
        switch (sliceDownloadType) {
            case SLICE_DOWNLOAD_TYPE_ZIP:
                sliceFileType = "zip";
                break;
            case SLICE_DOWNLOAD_TYPE_THUMBNAIL:
                sliceFileType = "jpg";
                break;
            case SLICE_DOWNLOAD_TYPE_PREVIEW:
                sliceFileType = "jpg";
                break;
            default:
                sliceFileType = "tif";
        }

        let [slicepattern, sliceprefix] = [undefined, undefined];
        if (sliceDownloadType == SLICE_DOWNLOAD_TYPE_ZIP) {
            sliceprefix = 'Binaries.zip';
            slicepattern = urlPattern;
        } else {
            [slicepattern, sliceprefix] = extractSliceName(urlPattern);
        }
        // E.g. [https://....jpg?se=..., slice_0_]
        console.log("EXTRACTED FILE NAME: ", sliceprefix, " WITH PATTERN: ", slicepattern);

        // Monkey patch for bug EXC-1230 Reconstruction large_image_pattern is wrong
        if (sliceDownloadType == SLICE_DOWNLOAD_TYPE_PREVIEW && slicepattern?.includes("Thumbnail/")) {
            slicepattern = slicepattern.replace("Thumbnail/", "");
        }

        const startindex = parseInt(minSlice);
        const endindex = parseInt(maxSlice);
        //DEBUG:
        //const endindex = Math.min(parseInt(maxSlice), startindex + 9);

        let MEM_REDUCTION_FACTOR = 1; // 1 = no reduction
        if (reductionFactor) {
            MEM_REDUCTION_FACTOR = reductionFactor; // E.g. 10 or 100
            console.log("REDUCTION FACTOR IS ", MEM_REDUCTION_FACTOR);
        }

        let binmodulo = 1;
        if (isNumeric(bin) && (bin == 1 || bin == 2 || bin == 4)) {
            binmodulo = bin;
        }

        // Reduce the Array by applying modulo MEM_REDUCTION_FACTOR
        const rawRangeArray = Array(endindex - startindex + 1);
        const rawRangeUnbinned = [...rawRangeArray.keys()].map((x) => x + startindex);
        const rawRange = rawRangeUnbinned.filter((x) => (x - startindex) % binmodulo == 0);
        console.log("MAX SLICE: ", maxSlice);
        console.log("RANGE: ", rawRange);
        const range = rawRange.filter((_, index) => {
            return index % MEM_REDUCTION_FACTOR == 0 || _ == maxSlice;
        });
        console.log("REDUCED RANGE: ", range);

        let fileUrlsToDownload = [];
        if (sliceDownloadType == SLICE_DOWNLOAD_TYPE_ZIP) {
            console.log('PREPARING TO DOWNLOAD SINGLE ZIP FILE...');
            fileUrlsToDownload = [{
                name: 'Binaries.zip',
                path: urlPattern,
                slice: 0,
                hejsan: 'svejsan'
            }];
        } else {
            fileUrlsToDownload = range.map((i) => {
                const j = String(i).padStart(4, "0");
                //return {name: `${sliceprefix}${j}.jpg`, path: slicepatternhack.replace('%04d', j)}
                return {
                    name: `${sliceprefix}${j}.${sliceFileType}`,
                    slice: i,
                    path: slicepattern.replace("%04d", j),
                };
            });
        }

        console.log(fileUrlsToDownload);

        if (includeReportUrl) {
            fileUrlsToDownload.unshift({
                name: 'report.pdf',
                slice: -1,
                path: includeReportUrl,
                type: 'application/octet-stream'
            });
        }

        // Remember that browsers allow only a limited number of connections to same
        // origin simultaneously. Chrome has a limit of 6.
        // Using more connections means that we just spawn waiting connections, which
        // means we don't get this many parallel downloads, but we don't have
        // to wait for the browser to recycle old connections for the next queued file.
        const DOWNLOAD_BATCH_SIZE = batchSize || 20;
        const batches = sliceIntoChunks(fileUrlsToDownload, DOWNLOAD_BATCH_SIZE);
        console.log(batches);

        // If we want to use File Access API, check if it is available first.
        // Otherwise use a fallback.
        // If the client wants to use (in-memory) download cache, then assume
        // we don't need to save at all, so no dir necessary.
        let dir = undefined;
        if (!useDownloadCache && USE_FILE_ACCESS_API) {
            if (USE_FILE_ACCESS_API && !window.showDirectoryPicker) {
                // Inform the user of the limitations (e.g. on Firefox)
                console.log("YOUR BROWSER DOES NOT SUPPORT FILE ACCESS API. TRY A CHROMIUM BROWSER INSTEAD!");
                const msg = `${now()}<br/>Your browser does not support File Access API. Try a Chromium based browser instead!`;
                toast.push(msg, toastThemeWarning);
            }

            if (USE_FILE_ACCESS_API && window.showDirectoryPicker) {
                try {
                    dir = await window.showDirectoryPicker({
                        mode: "readwrite",
                    });
                    console.log("DIR: ", dir);
                } catch (error) {
                    console.log("COULD NOT SHOW DIRECTORYPICKER: ", error);
                    return;
                }
            }
        }

        console.log("DIR IS NOW: ", dir);

        // Start timer
        const start = Date.now();
        let count = 0;
        let bytes = 0;
        let fetchTime = 0;
        let loadTime = 0;
        let writeTime = 0;
        const stats = {
            count: count,
            bytes: bytes,
            fetchTime: fetchTime,
            loadTime: loadTime,
            writeTime: writeTime,
        }; // TODO: Get rid of primitive variable above

        noSleep.enable();
        let msg = `${now()}<br/>Started download: ${$currentSession} / ${name} <br/> <p style="color:yellow; text-shadow: 0 -1px lightgreen, 1px -1px lightgreen, 1px 0 lightgreen, 1px 1px lightgreen, 0 1px lightgreen, -1px 1px lightgreen, -1px 0 lightgreen, -1px -1px lightgreen;">DO NOT CLOSE TAB UNTIL DONE!</p>`;
        let toastId;
        if (!useSliceDownloadCache) {
            toastId = toast.push(msg, toastThemeInfo);
        }

        const job = {
            user: $username,
            session: $currentSession,
            name: name,
            directory: dir?.name || "",
            batchSize: DOWNLOAD_BATCH_SIZE,
            started: start,
            minSlice: minSlice,
            maxSlice: maxSlice,
            expectedCount: includeReportUrl ? range.length + 1 : range.length,
            status: "running",
            toast: toastId,
        };
        addDownloadJob(job);
        if (useSliceDownloadCache) {
            $memcacheProgress = {
                expectedCount: job.expectedCount,
                count: job.count,
            };
        }
        $downloadJobs = $downloadJobs;

        // Define an abort controller for this job
        let controller = new AbortController();

        // Update job regularly
        let interval = setInterval(() => {
            console.log("FILE COUNT = ", stats.count, ", FAILED COUNT: ", failedDownloads?.length);
            job.count = stats.count;
            job.bytes = stats.bytes;
            updateJob(job);
            $memcacheProgress.count = job.count;

            // Check if anyone has interrupted this job
            if (isJobInterrupted(job)) {
                job.interrupted = true;
                controller?.abort();
            }
            // Update failed downloads
            failedDownloads = failedDownloads;
        }, 1000);

        try {
            for (const batch of batches) {
                if (job.interrupted === true) {
                    throw new Error("Interrupted");
                }
                console.log("STARTING NEW BATCH...", batch);

                await Promise.all(
                    batch.map((f) => downloadFile(f, controller, dir, useSliceDownloadCache, stats))
                ).catch((error) => {
                    console.log("DOWNLOAD BATCH INTERRUPTED!");
                    throw error;
                });

                console.log("FINISHED BATCH...");
                job.count = stats.count;
                job.bytes = stats.bytes;
                job.fetchTime = Math.round(stats.fetchTime);
                job.loadTime = Math.round(stats.loadTime);
                job.writeTime = Math.round(stats.writeTime);
                updateJob(job);
            }

            job.count = stats.count;
            job.bytes = stats.bytes;
            job.status = "done";
            job.finished = Date.now();
            updateJob(job);
            msg = `${now()}<br/>Finished download: ${$currentSession} / ${name}`;

            if (job.directory) {
                msg += `<p>Saved to ${job.directory}</p>`;
            } else {
                msg += `<p>Saved to your browser's downloads folder</p>`;
            }

            if (!useSliceDownloadCache) {
                toast.push(msg, toastThemeInfo);
            }
            toast.pop(toastId); // Remove the start toast
        } catch (err) {
            console.log("ERROR SAVING FILES: ", err);
            job.count = stats.count;
            job.bytes = stats.bytes;
            job.status = "failed";
            job.finished = Date.now();
            updateJob(job);
            msg = `${now()}<br/>Failed download: ${$currentSession} / ${name}`;
            toast.push(msg, toastThemeErrorDismissable);
        } finally {
            console.log("FINALLY DOWNLOADED ENDED!");
            noSleep.disable();
            setTimeout(() => clearInterval(interval), 1500);
        }

        // DEBUG:
        const stop = Date.now();
        console.log("SAVED ", stats.count, " IN ", stop - start, " MILLISECONDS");
        console.log("FAILED DOWNLOAD: ", failedDownloads);
    }

    // Fallback solution (e.g. Firefox) where File Access API is not available
    // Basically create an anchor element pointing to the URL to download,
    // and simulate clicking the element to kick off download.
    // Stores blob in browser's Downloads folder without folder structure.
    function click(node) {
        try {
            console.log("PROGRAMMATICALLY CLICKING ", node);
            node.dispatchEvent(new MouseEvent("click"));
        } catch (error) {
            console.log("ERROR CLICK DOWNLOAD LINK FAILED!", error);
        }
    }

    function saveBlob(blob) {
        let a = document.createElement("a");
        a.download = blob.name;
        a.rel = "noopener"; // tabnabbing
        if (blob.name.includes("pdf")) {
            a.type = "application/octet-stream";
        }
        var url = URL.createObjectURL(blob);
        console.log("BLOB IS ", blob);
        console.log("CREATED OBJECT URL ", url);
        a.href = url;
        setTimeout(function () {
            URL.revokeObjectURL(a.href);
        }, 4e4); // 40s
        setTimeout(function () {
            click(a);
        }, 0);
    }
</script>

{#if $showDownloader}
    {#if $downloadJobs?.length > 0}
        <Modal wide={true} on:ok={() => ($showDownloader = false)} okable={true} cancellable={false} jsonable={false}>
            <h2 slot="header">
                Downloads
                <annotation style="font-weight: normal;">(experimental)</annotation>
            </h2>

            <downloads-pane>
                <!--h3>Downloads<annotation style="font-weight: normal;">(experimental)</annotation></h3-->
                <table>
                    <th>Started</th>
                    <th>Session</th>
                    <th>Name</th>
                    <th>Folder</th>
                    <th>Progress</th>
                    <th>Status</th>
                    <th>Modified</th>
                    <th>Finished</th>
                    <th>Duration</th>
                    <th>Bytes</th>
                    <th>Speed</th>
                    <!--DEBUG:-->
                    <th>Conns</th>
                    <th>Fetch</th>
                    <th>Load</th>
                    <th>Write</th>
                    <!---->
                    {#each $downloadJobs as j}
                        <tr>
                            <td>{millisecondsToTime(j.started)}</td>
                            <td>{j.session}</td>
                            <td>{j.name}</td>
                            <td>{j.directory || ""}</td>
                            <td>{j.count || 0} / {j.expectedCount}</td>
                            <td>{j.status}</td>
                            <td>
                                {j.status === "running" &&
                                Date.now() - (j.modified ? j.modified : j.started) > 2 * 60 * 1000
                                    ? "Stuck?"
                                    : millisecondsToTime(j.modified) || ""}
                            </td>
                            <td>{millisecondsToTime(j.finished) || ""}</td>
                            <td>{millisecondsToTime(durationInMilliseconds(j.started, j.modified)) || ""}</td>
                            <td>{j.bytes || ""}</td>
                            <td>
                                {(
                                    (j.bytes / 1024 / 1024 / durationInMilliseconds(j.started, j.modified)) *
                                    1000
                                )?.toFixed(3) || ""} MB/s
                            </td>
                            <!--DEBUG:-->
                            <td>{j.batchSize || ""}</td>
                            <td>{Math.round(j.fetchTime / 1000) || 0} s</td>
                            <td>{Math.round(j.loadTime / 1000) || 0} s</td>
                            <td>{Math.round(j.writeTime / 1000) || 0} s</td>
                            <!---->
                            <td class="downloads-last-col">
                                {#if j.interrupted}
                                    Interrupted
                                {:else if j.status == "running"}
                                    <button on:click={interruptJob(j)}>Stop</button>
                                {:else}
                                    &nbsp;
                                {/if}
                            </td>
                        </tr>
                    {/each}
                </table>
                Cached: {Object.keys($memcache)?.length}
            </downloads-pane>

            <annotation>NOTE! Old jobs will disappear after 30 minutes.</annotation>
            <br />
            <failed-downloads>
                {#if failedDownloads?.length > 0}
                    <annotation>Failed:</annotation>
                    {#each failedDownloads as f}
                        <annotation>{f?.name}</annotation>
                    {/each}
                {/if}
            </failed-downloads>
        </Modal>
    {/if}
    <!-- $downloadJobs -->
{/if}

<!-- $showDownloader -->
<style>
    downloads-pane {
        width: 100%;
        height: 100%;
        display: inline-block;
        padding: 0 8px 2px 8px;
        background-color: var(--var-theme-base-background-color);
        vertical-align: top;
        /* overflow-x: hidden; */
        overflow-y: scroll;
        text-align: left;
    }

    .downloads-last-col {
        border: 0;
        padding-top: 2px;
        padding-bottom: 2px;
    }
    tr:hover td.downloads-last-col {
        border: 0;
        background-color: transparent;
    }

    downloads-pane button {
        margin: 0;
        padding: 4px 8px 4px 8px;
        background-color: red;
    }

    downloads-pane button:hover {
        background-color: var(--var-theme-hover-background-color);
    }

    failed-downloads {
        width: 100%;
        background-color: aliceblue;
    }
</style>
